import React, { createContext, useEffect, useState } from "react";
import Footer from "../../global/components/Footer";
import { IIlpContextType } from "../types/interfaces/IIlpContextType";
import { IIlpProviderProps } from "../types/interfaces/IIlpProviderProps";
import { useAuthorizationContext } from "../../global/hooks/useAuthorizationContext";
import { useAuthenticationContext } from "../../global/hooks/useAuthenticationContext";
import { useDuesSponsoredOrgContext } from '../../global/hooks/useDuesSponsoredOrgContext';
import { IOrganization } from "../../global/interfaces/IOrganization";
import { Organization } from "../../global/interfaces/Organization";


//The ilp context object holds the latest value of the state.
export const IlpContext = createContext<IIlpContextType | undefined>(undefined);

//This component is responsible to share the ilp data between components.
export const IlpProvider: React.FC<IIlpProviderProps> = ({ children }) => {
    const [role, setRole] = useState<string | undefined>("unknown");
    const [customerKey, setCustomerKey] = useState<string | undefined>("00000000-0000-0000-0000-000000000000");
    const [standAloneOrg, setStandAloneOrg] = useState<IOrganization | undefined>();
    const [isLearnerFlag, setIsLearnerFlag] = useState<boolean>(false);
    const [isAdminFlag, setIsAdminFlag] = useState<boolean>(false);
    const [isAdvisorFlag, setIsAdvisorFlag] = useState<boolean>(false);
    const [isInProcess, setIsInProcess] = useState<boolean>(false);
    const [expandAll, setExpandAll] = useState<boolean>(false);
    const [loadingIlpCtx, setLoadingIlpCtx] = useState<boolean>(true);
    const [areChildrenLoading, setAreChildrenLoading] = useState<boolean>(true);

    const { roles } = useAuthorizationContext();
    const { cstKey } = useAuthenticationContext();
    const { organizations, selectedOrganization, setOrganizations, setSelectedOrganization } = useDuesSponsoredOrgContext();
    const ILP_NFROLE_LIST: string = process.env.REACT_APP_ILP_NFROLE_LIST!;
    const ILP_LEARNER_NFROLE_LIST: string = process.env.REACT_APP_ILP_LEARNER_NFROLE_LIST!;
    const ILP_IXO_TITLE_LIST: string | null = process.env.REACT_APP_ILP_IXO_TITLE_LIST!;
    const STAND_ALONE_ORG_NAME: string = process.env.REACT_APP_ILP_STAND_ALONE_ORG_NAME!;

    const ilpRolesArray = ILP_NFROLE_LIST.split(",");
    const ilpLearnerRolesArray = ILP_LEARNER_NFROLE_LIST.split(",");
    const ilpTitlesArray = ILP_IXO_TITLE_LIST.split(",");
    const foundRoles = ilpRolesArray.filter(role => roles?.includes(role));
    const foundRoleCount = foundRoles.length ?? 0;
    let orgCount = organizations?.length ?? 0;

    const findAndSetSelectedRole = (selectedOrganization: IOrganization | undefined): string | undefined => {
        // user has a selectedOrganization. Set role from org ixo_title.
        if (selectedOrganization !== undefined) {
            let selectedOrgIxoTitle: string | null | undefined;
            selectedOrgIxoTitle = selectedOrganization.Title;
            return getRoleFromIxoTitle(selectedOrgIxoTitle);
        }
        // user has no selectedOrganization and only one organization. 
        // Set selected org and set role from org ixo_title.
        else if (organizations !== undefined && organizations.length > 0 && orgCount === 1) {
            setSelectedOrganization(organizations[0]);
            let selectedOrgIxoTitle: string | null | undefined;
            selectedOrgIxoTitle = organizations[0].Title;
            return getRoleFromIxoTitle(selectedOrgIxoTitle);
        }
        // user has no selectedOrganization and no organizations. Set role from list from getRoles.
        else if (orgCount === 0) {
            return getRoleFromRoles();
        }
        // user has no selectedOrganization and multiple organizations. User must select organization.
        else if (organizations !== undefined && orgCount > 1) {
            // These users must be redirected (in IlpRouter) to the Organization selector.
            // IlpRouter will expect undefined selectedOrganization and undefined role for this scenario.
            setSelectedOrganization(undefined);
            setRole(undefined);
            return undefined;
        }
    }

    const getRoleFromIxoTitle = (ixoTitle: string | null | undefined): string | undefined => {
        if (ixoTitle === null || ixoTitle === undefined) return undefined;
        return determineRole(ixoTitle);
    }

    // This method is called only when user has no organizations.
    // If user has more than one role, we do NOT set it here. 
    const getRoleFromRoles = (): string | undefined => {
        if (foundRoles === undefined || foundRoles.length > 1) return undefined;
        return determineRole(foundRoles[0]);
    }

    // This aligns parameter "roleIndicator" with specific nf role.
    // Param "roleIndicator" can be either ixo_title from selectedOrg or nf role from getRoles.
    const determineRole = (roleIndicator: string | null | undefined): string | undefined => {
        if (roleIndicator === null || roleIndicator === undefined) return undefined;

        setIsLearnerFlag(false);
        setIsAdminFlag(false);
        setIsAdvisorFlag(false);

        switch (roleIndicator) {
            case process.env.REACT_APP_NFROLE_STUDENT:
                setRole(roleIndicator);
                setIsLearnerFlag(true);
                return roleIndicator;
            case process.env.REACT_APP_NFROLE_RESIDENT:
            case process.env.REACT_APP_IXO_TITLE_RESIDENT:
            case process.env.REACT_APP_IXO_TITLE_CHIEF_RESIDENT:
                setRole(process.env.REACT_APP_NFROLE_RESIDENT);
                setIsLearnerFlag(true);
                return process.env.REACT_APP_NFROLE_RESIDENT;   
            case process.env.REACT_APP_NFROLE_POST_RESIDENT:            
            case process.env.REACT_APP_IXO_TITLE_POST_RESIDENT:
                setRole(process.env.REACT_APP_NFROLE_POST_RESIDENT);
                setIsLearnerFlag(true);
                return process.env.REACT_APP_NFROLE_POST_RESIDENT;      
            case process.env.REACT_APP_NFROLE_DIRECTOR:
            case process.env.REACT_APP_NFROLE_COORDINATOR:
            case process.env.REACT_APP_NFROLE_ADMINISTRATOR:
            case process.env.REACT_APP_NFROLE_ASSOCIATE_DIRECTOR:
            case process.env.REACT_APP_NFROLE_ASSISTANT_COORDINATOR:
                setRole(roleIndicator);
                setIsAdminFlag(true);
                return roleIndicator;
            case process.env.REACT_APP_NFROLE_ADVISOR:
                setRole(roleIndicator);
                setIsAdvisorFlag(true);
                return roleIndicator;
            default:
                return undefined;
        }
    }


    const filterIlpOrganizations = () => {
        if (organizations === undefined) return;
        if (ilpTitlesArray === null || ilpTitlesArray.length === 0) return;
        const filteredOrgs = organizations.filter(org => ilpTitlesArray.includes(org.Title!));
        setOrganizations(filteredOrgs);
        orgCount = filteredOrgs.length;
    }

    const checkForRolesWithNoOrg = () => {
        if (foundRoleCount <= orgCount) return;
        foundRoles.forEach((foundRole) => {
            const foundMatch = organizations?.find(org => org.Title === foundRole);
            if (!foundMatch && ilpLearnerRolesArray.includes(foundRole)) {
                const newStandAloneOrg = new Organization(STAND_ALONE_ORG_NAME, foundRole);
                setStandAloneOrg(newStandAloneOrg);
                orgCount++;        
            }
          });
    }

    const setUser = () => {
            setCustomerKey(cstKey);
            checkForRolesWithNoOrg();
            findAndSetSelectedRole(selectedOrganization);  
    };

    useEffect(() => {
        try {
            filterIlpOrganizations();
            setUser();
        }
        finally {
            setLoadingIlpCtx(false);
        }
    }, []);

    const value = {
        customerKey,
        role,
        standAloneOrg,
        isLearnerFlag,
        isAdminFlag,
        isAdvisorFlag,
        isInProcess,
        expandAll,
        loadingIlpCtx,
        setRole,
        setIsLearnerFlag,
        setIsAdvisorFlag,
        setIsAdminFlag,
        setIsInProcess,
        setExpandAll,
        setLoadingIlpCtx,
        findAndSetSelectedRole,
        areChildrenLoading,
        setAreChildrenLoading
    };

    return <IlpContext.Provider value={value}>
        {!loadingIlpCtx && (children)}
        {!areChildrenLoading && <Footer />}
    </IlpContext.Provider>;
};
